import { useMemo, useState } from 'react';
import { api, ApiError } from '../api.ts';
import { Card, Checkbox, Field, Modal, Tag, useToast } from '../ui.tsx';
import { CATEGORICAL } from '../charts.tsx';
import { fmtDuration } from '../format.ts';

/** Editor da timeline: elementos interativos e CTAs temporizados. */

export interface ElementModel {
  id: string; kind: string; preset: string; name: string; enabled: 0 | 1;
  trigger: string; start_sec: number; end_sec: number | null; percent: number | null;
  title: string; description: string; image_url: string; url: string; action: string;
  event_name: string | null;
  style: {
    bgColor: string; textColor: string; borderColor: string; borderRadius: number; fontSize: number;
    fontWeight: number; paddingX: number; paddingY: number; width: number | null; shadow: boolean; animation: string;
  };
  position: { x: number; y: number; anchor: string };
  mobile: { visible: boolean; position: { x: number; y: number; anchor: string }; scale: number; fullWidth: boolean };
  showOnce: boolean; pauseVideo: boolean; openNewTab: boolean; applyUtms: boolean; dismissible: boolean;
  countdownSeconds: number | null;
  form: { fields: FormFieldModel[]; submitLabel: string; successMessage: string; webhookUrl: string } | null;
  checkout: { provider: string; productId: string; priceCents: number; currency: string; bumpUrl: string } | null;
  sort_order: number;
}

interface FormFieldModel {
  id: string; name: string; label: string; type: string; required: boolean; placeholder: string; options: string[];
}

const KINDS: { value: string; label: string }[] = [
  { value: 'cta', label: 'CTA (botão de ação)' },
  { value: 'button', label: 'Botão' },
  { value: 'text', label: 'Texto' },
  { value: 'image', label: 'Imagem' },
  { value: 'form', label: 'Formulário' },
  { value: 'countdown', label: 'Contador' },
  { value: 'notice', label: 'Aviso' },
  { value: 'link', label: 'Link' },
  { value: 'redirect', label: 'Redirecionamento' },
  { value: 'checkout', label: 'Bloco de checkout' },
];

const PRESETS: { value: string; label: string }[] = [
  { value: 'generic', label: 'Genérico' },
  { value: 'purchase', label: 'CTA de compra' },
  { value: 'whatsapp', label: 'CTA de WhatsApp' },
  { value: 'capture', label: 'CTA de captura' },
  { value: 'form', label: 'CTA de formulário' },
  { value: 'checkout', label: 'CTA de checkout' },
  { value: 'upsell', label: 'Upsell' },
  { value: 'downsell', label: 'Downsell' },
  { value: 'order_bump', label: 'Order bump' },
];

const TRIGGERS: { value: string; label: string }[] = [
  { value: 'start', label: 'No início' },
  { value: 'time', label: 'Em um tempo específico' },
  { value: 'percent', label: 'Por percentual assistido' },
  { value: 'end', label: 'No final' },
  { value: 'pause', label: 'Quando pausar' },
  { value: 'manual', label: 'Manual (não exibe sozinho)' },
];

const ACTIONS: { value: string; label: string }[] = [
  { value: 'none', label: 'Nenhuma' },
  { value: 'open_url_new_tab', label: 'Abrir URL em nova aba' },
  { value: 'open_url', label: 'Abrir URL na mesma aba' },
  { value: 'redirect', label: 'Redirecionar a página' },
  { value: 'checkout', label: 'Ir para o checkout' },
  { value: 'whatsapp', label: 'Abrir WhatsApp' },
  { value: 'submit_form', label: 'Enviar formulário' },
  { value: 'scroll_to', label: 'Rolar a página' },
  { value: 'replay', label: 'Reiniciar o vídeo' },
  { value: 'unmute', label: 'Ativar o som' },
  { value: 'seek', label: 'Pular para o ponto' },
  { value: 'close', label: 'Fechar o elemento' },
];

const ANCHORS = [
  'top-left', 'top-center', 'top-right',
  'middle-left', 'center', 'middle-right',
  'bottom-left', 'bottom-center', 'bottom-right',
];

const EVENTS = ['cta_clicked', 'checkout_clicked', 'lead_submitted', 'purchase', 'form_started'];

export function VideoTimelinePanel({
  videoId, elements, duration, canWrite, onChanged,
}: {
  videoId: string;
  elements: ElementModel[];
  duration: number;
  canWrite: boolean;
  onChanged: () => void;
}): JSX.Element {
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(elements[0]?.id ?? null);
  const [adding, setAdding] = useState(false);

  const selected = useMemo(
    () => elements.find((element) => element.id === selectedId) ?? null,
    [elements, selectedId],
  );

  const totalDuration = duration > 0 ? duration : 600;

  function startOf(element: ElementModel): number {
    if (element.trigger === 'percent' && element.percent !== null) return (totalDuration * element.percent) / 100;
    if (element.trigger === 'end') return Math.max(0, totalDuration - 10);
    return element.start_sec;
  }
  function endOf(element: ElementModel): number {
    if (element.end_sec !== null) return element.end_sec;
    if (element.trigger === 'start') return startOf(element) + 8;
    return totalDuration;
  }

  async function create(preset: string): Promise<void> {
    try {
      const data = await api<{ element: ElementModel }>(`/api/videos/${videoId}/elements`, {
        body: {
          preset,
          trigger: 'time',
          start_sec: Math.round(totalDuration * 0.3),
          end_sec: Math.round(totalDuration * 0.9),
          title: PRESETS.find((p) => p.value === preset)?.label ?? 'Novo CTA',
          name: PRESETS.find((p) => p.value === preset)?.label ?? 'Novo CTA',
        },
      });
      setSelectedId(data.element.id);
      setAdding(false);
      toast('Elemento criado.', 'ok');
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao criar elemento.', 'error');
    }
  }

  async function patch(id: string, changes: Record<string, unknown>): Promise<void> {
    try {
      await api(`/api/elements/${id}`, { method: 'PATCH', body: changes });
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao salvar.', 'error');
    }
  }

  async function remove(id: string): Promise<void> {
    try {
      await api(`/api/elements/${id}`, { method: 'DELETE' });
      setSelectedId(null);
      toast('Elemento removido.', 'ok');
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao remover.', 'error');
    }
  }

  return (
    <div className="editor-layout">
      <div className="grid" style={{ gap: 18 }}>
        <Card
          title="Linha do tempo"
          subtitle={`Duração do vídeo: ${fmtDuration(duration)}${duration ? '' : ' (envie o arquivo para calibrar a régua)'}`}
          actions={canWrite ? <button type="button" className="btn small" onClick={() => setAdding(true)}>Adicionar elemento</button> : undefined}
        >
          <div className="timeline">
            <div className="timeline-track">
              {elements.map((element, index) => {
                const start = Math.max(0, Math.min(startOf(element), totalDuration));
                const end = Math.max(start + 1, Math.min(endOf(element), totalDuration));
                const left = (start / totalDuration) * 100;
                const width = Math.max(4, ((end - start) / totalDuration) * 100);
                return (
                  <button
                    type="button"
                    key={element.id}
                    className="timeline-el"
                    aria-pressed={element.id === selectedId}
                    onClick={() => setSelectedId(element.id)}
                    title={`${element.name} · ${fmtDuration(start)} → ${fmtDuration(end)}`}
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      background: element.enabled ? CATEGORICAL[index % CATEGORICAL.length] : 'var(--surface-3)',
                      opacity: element.enabled ? 1 : 0.55,
                      color: element.enabled ? '#0b0611' : 'var(--text-3)',
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{element.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="timeline-ruler">
            <span>0:00</span>
            <span>{fmtDuration(totalDuration * 0.25)}</span>
            <span>{fmtDuration(totalDuration * 0.5)}</span>
            <span>{fmtDuration(totalDuration * 0.75)}</span>
            <span>{fmtDuration(totalDuration)}</span>
          </div>
        </Card>

        <Card title="Elementos" subtitle={`${elements.length} elemento(s) neste vídeo`}>
          {elements.length === 0 ? (
            <p style={{ color: 'var(--text-3)', margin: 0 }}>
              Nenhum elemento ainda. Adicione um CTA de compra, um formulário de captura ou um aviso de abertura.
            </p>
          ) : (
            <div className="element-list">
              {elements.map((element, index) => (
                <div
                  key={element.id}
                  className="element-item"
                  role="button"
                  tabIndex={0}
                  aria-pressed={element.id === selectedId}
                  onClick={() => setSelectedId(element.id)}
                  onKeyDown={(event) => { if (event.key === 'Enter') setSelectedId(element.id); }}
                >
                  <i className="dot" style={{ background: CATEGORICAL[index % CATEGORICAL.length] }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{element.name}</b>
                    <span>
                      {KINDS.find((k) => k.value === element.kind)?.label ?? element.kind} ·{' '}
                      {element.trigger === 'percent' ? `aos ${element.percent}%` : `${fmtDuration(startOf(element))} → ${fmtDuration(endOf(element))}`}
                    </span>
                  </div>
                  {element.enabled === 0 && <Tag tone="muted">desativado</Tag>}
                  {!element.mobile.visible && <Tag tone="warn">sem mobile</Tag>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div>
        {selected ? (
          <ElementForm
            key={selected.id}
            element={selected}
            duration={totalDuration}
            canWrite={canWrite}
            onSave={(changes) => patch(selected.id, changes)}
            onDelete={() => remove(selected.id)}
          />
        ) : (
          <Card title="Configuração do elemento">
            <p style={{ color: 'var(--text-3)', margin: 0 }}>Selecione um elemento na lista ou na linha do tempo.</p>
          </Card>
        )}
      </div>

      {adding && (
        <Modal title="Adicionar elemento" onClose={() => setAdding(false)} wide>
          <p style={{ marginTop: 0, color: 'var(--text-3)' }}>
            Escolha um modelo. Ele já vem com ação, evento e cor coerentes — tudo pode ser ajustado depois.
          </p>
          <div className="grid cols-3">
            {PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className="btn secondary"
                style={{ justifyContent: 'flex-start', padding: '14px 16px' }}
                onClick={() => create(preset.value)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ElementForm({
  element, duration, canWrite, onSave, onDelete,
}: {
  element: ElementModel;
  duration: number;
  canWrite: boolean;
  onSave: (changes: Record<string, unknown>) => void;
  onDelete: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<ElementModel>(element);
  const [dirty, setDirty] = useState(false);

  function update(changes: Partial<ElementModel>): void {
    setDraft((current) => ({ ...current, ...changes }));
    setDirty(true);
  }
  function updateStyle(changes: Partial<ElementModel['style']>): void {
    setDraft((current) => ({ ...current, style: { ...current.style, ...changes } }));
    setDirty(true);
  }
  function updatePosition(changes: Partial<ElementModel['position']>): void {
    setDraft((current) => ({ ...current, position: { ...current.position, ...changes } }));
    setDirty(true);
  }
  function updateMobile(changes: Partial<ElementModel['mobile']>): void {
    setDraft((current) => ({ ...current, mobile: { ...current.mobile, ...changes } }));
    setDirty(true);
  }

  const showsForm = draft.kind === 'form';
  const showsCheckout = draft.kind === 'checkout' || draft.preset === 'checkout' || draft.preset === 'upsell' || draft.preset === 'downsell' || draft.preset === 'order_bump';

  return (
    <Card
      title="Configuração do elemento"
      subtitle={draft.name}
      actions={
        canWrite ? (
          <div className="row tight">
            <button type="button" className="btn small" disabled={!dirty} onClick={() => { onSave(draft as unknown as Record<string, unknown>); setDirty(false); }}>
              Salvar
            </button>
            <button type="button" className="btn ghost small" onClick={onDelete}>Excluir</button>
          </div>
        ) : undefined
      }
    >
      <fieldset disabled={!canWrite} style={{ border: 0, padding: 0, margin: 0 }}>
        <Field label="Nome interno" id="el-name">
          <input id="el-name" value={draft.name} onChange={(e) => update({ name: e.target.value })} />
        </Field>

        <div className="grid cols-2">
          <Field label="Tipo" id="el-kind">
            <select id="el-kind" value={draft.kind} onChange={(e) => update({ kind: e.target.value })}>
              {KINDS.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
            </select>
          </Field>
          <Field label="Modelo de CTA" id="el-preset">
            <select id="el-preset" value={draft.preset} onChange={(e) => update({ preset: e.target.value })}>
              {PRESETS.map((preset) => <option key={preset.value} value={preset.value}>{preset.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Quando aparecer" id="el-trigger">
          <select id="el-trigger" value={draft.trigger} onChange={(e) => update({ trigger: e.target.value })}>
            {TRIGGERS.map((trigger) => <option key={trigger.value} value={trigger.value}>{trigger.label}</option>)}
          </select>
        </Field>

        {draft.trigger === 'percent' ? (
          <Field label={`Percentual assistido: ${draft.percent ?? 50}%`} id="el-percent">
            <input
              id="el-percent" type="range" min={0} max={100} step={1}
              value={draft.percent ?? 50}
              onChange={(e) => update({ percent: Number(e.target.value) })}
            />
          </Field>
        ) : (
          <div className="grid cols-2">
            <Field label="Tempo inicial (s)" id="el-start" hint={fmtDuration(draft.start_sec)}>
              <input
                id="el-start" type="number" min={0} max={Math.round(duration)} step={1}
                value={draft.start_sec}
                onChange={(e) => update({ start_sec: Number(e.target.value) })}
              />
            </Field>
            <Field label="Tempo final (s)" id="el-end" hint={draft.end_sec === null ? 'Até o fim do vídeo' : fmtDuration(draft.end_sec)}>
              <input
                id="el-end" type="number" min={0} max={Math.round(duration)} step={1}
                value={draft.end_sec ?? ''}
                placeholder="até o fim"
                onChange={(e) => update({ end_sec: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </Field>
          </div>
        )}

        <Field label="Título / texto do botão" id="el-title">
          <input id="el-title" value={draft.title} onChange={(e) => update({ title: e.target.value })} />
        </Field>
        <Field label="Descrição" id="el-desc">
          <textarea id="el-desc" value={draft.description} onChange={(e) => update({ description: e.target.value })} />
        </Field>

        {draft.kind === 'image' && (
          <Field label="URL da imagem" id="el-img">
            <input id="el-img" value={draft.image_url} onChange={(e) => update({ image_url: e.target.value })} placeholder="https://…" />
          </Field>
        )}

        <Field label="URL de destino" id="el-url" hint="Somente http(s), mailto ou tel.">
          <input id="el-url" value={draft.url} onChange={(e) => update({ url: e.target.value })} placeholder="https://…" />
        </Field>

        <div className="grid cols-2">
          <Field label="Ação ao clicar" id="el-action">
            <select id="el-action" value={draft.action} onChange={(e) => update({ action: e.target.value })}>
              {ACTIONS.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}
            </select>
          </Field>
          <Field label="Evento extra" id="el-event" hint="Além de cta_clicked.">
            <select id="el-event" value={draft.event_name ?? ''} onChange={(e) => update({ event_name: e.target.value || null })}>
              <option value="">Nenhum</option>
              {EVENTS.map((event) => <option key={event} value={event}>{event}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid cols-3">
          <Field label="Cor de fundo" id="el-bg"><input id="el-bg" type="color" value={draft.style.bgColor} onChange={(e) => updateStyle({ bgColor: e.target.value })} /></Field>
          <Field label="Cor do texto" id="el-fg"><input id="el-fg" type="color" value={draft.style.textColor} onChange={(e) => updateStyle({ textColor: e.target.value })} /></Field>
          <Field label="Borda" id="el-bd"><input id="el-bd" type="color" value={draft.style.borderColor} onChange={(e) => updateStyle({ borderColor: e.target.value })} /></Field>
        </div>

        <div className="grid cols-3">
          <Field label={`Fonte: ${draft.style.fontSize}px`} id="el-fs">
            <input id="el-fs" type="range" min={10} max={40} value={draft.style.fontSize} onChange={(e) => updateStyle({ fontSize: Number(e.target.value) })} />
          </Field>
          <Field label={`Cantos: ${draft.style.borderRadius}px`} id="el-br">
            <input id="el-br" type="range" min={0} max={40} value={draft.style.borderRadius} onChange={(e) => updateStyle({ borderRadius: Number(e.target.value) })} />
          </Field>
          <Field label={`Largura: ${draft.style.width ? `${draft.style.width}%` : 'auto'}`} id="el-w">
            <input
              id="el-w" type="range" min={0} max={100}
              value={draft.style.width ?? 0}
              onChange={(e) => updateStyle({ width: Number(e.target.value) === 0 ? null : Number(e.target.value) })}
            />
          </Field>
        </div>

        <div className="grid cols-3">
          <Field label={`Posição X: ${draft.position.x}%`} id="el-x">
            <input id="el-x" type="range" min={0} max={100} value={draft.position.x} onChange={(e) => updatePosition({ x: Number(e.target.value) })} />
          </Field>
          <Field label={`Posição Y: ${draft.position.y}%`} id="el-y">
            <input id="el-y" type="range" min={0} max={100} value={draft.position.y} onChange={(e) => updatePosition({ y: Number(e.target.value) })} />
          </Field>
          <Field label="Ancoragem" id="el-anchor">
            <select id="el-anchor" value={draft.position.anchor} onChange={(e) => updatePosition({ anchor: e.target.value })}>
              {ANCHORS.map((anchor) => <option key={anchor} value={anchor}>{anchor}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Animação" id="el-anim">
          <select id="el-anim" value={draft.style.animation} onChange={(e) => updateStyle({ animation: e.target.value })}>
            <option value="none">Sem animação</option>
            <option value="fade">Fade</option>
            <option value="slide-up">Deslizar de baixo</option>
            <option value="pop">Pop</option>
            <option value="pulse">Pulsar</option>
          </select>
        </Field>

        <h3 className="card-title" style={{ marginTop: 18 }}>Comportamento</h3>
        <Checkbox checked={draft.enabled === 1} onChange={(value) => update({ enabled: value ? 1 : 0 })} label="Elemento ativo" />
        <Checkbox checked={draft.showOnce} onChange={(value) => update({ showOnce: value })} label="Aparecer uma única vez" />
        <Checkbox checked={draft.pauseVideo} onChange={(value) => update({ pauseVideo: value })} label="Pausar o vídeo ao aparecer" />
        <Checkbox checked={draft.openNewTab} onChange={(value) => update({ openNewTab: value })} label="Abrir em nova aba" />
        <Checkbox checked={draft.applyUtms} onChange={(value) => update({ applyUtms: value })} label="Aplicar UTMs automaticamente no destino" />
        <Checkbox checked={draft.dismissible} onChange={(value) => update({ dismissible: value })} label="Permitir fechar" />

        <h3 className="card-title" style={{ marginTop: 18 }}>Mobile</h3>
        <Checkbox checked={draft.mobile.visible} onChange={(value) => updateMobile({ visible: value })} label="Exibir no mobile" />
        <Checkbox checked={draft.mobile.fullWidth} onChange={(value) => updateMobile({ fullWidth: value })} label="Largura total no mobile" />
        <Field label={`Escala no mobile: ${draft.mobile.scale.toFixed(2)}×`} id="el-scale">
          <input
            id="el-scale" type="range" min={0.5} max={1.5} step={0.05}
            value={draft.mobile.scale}
            onChange={(e) => updateMobile({ scale: Number(e.target.value) })}
          />
        </Field>

        {draft.kind === 'countdown' && (
          <Field label="Contagem regressiva (segundos)" id="el-count">
            <input
              id="el-count" type="number" min={1} max={86400}
              value={draft.countdownSeconds ?? 300}
              onChange={(e) => update({ countdownSeconds: Number(e.target.value) })}
            />
          </Field>
        )}

        {showsForm && (
          <>
            <h3 className="card-title" style={{ marginTop: 18 }}>Formulário</h3>
            <FormFieldsEditor
              value={draft.form ?? { fields: defaultFields(), submitLabel: 'Enviar', successMessage: 'Recebemos seus dados!', webhookUrl: '' }}
              onChange={(form) => update({ form })}
            />
          </>
        )}

        {showsCheckout && (
          <>
            <h3 className="card-title" style={{ marginTop: 18 }}>Checkout</h3>
            <div className="grid cols-2">
              <Field label="Plataforma" id="el-prov">
                <input
                  id="el-prov"
                  value={draft.checkout?.provider ?? ''}
                  onChange={(e) => update({ checkout: { ...(draft.checkout ?? emptyCheckout()), provider: e.target.value } })}
                  placeholder="hotmart, stripe, kiwify…"
                />
              </Field>
              <Field label="ID do produto" id="el-prod">
                <input
                  id="el-prod"
                  value={draft.checkout?.productId ?? ''}
                  onChange={(e) => update({ checkout: { ...(draft.checkout ?? emptyCheckout()), productId: e.target.value } })}
                />
              </Field>
              <Field label="Preço (centavos)" id="el-price">
                <input
                  id="el-price" type="number" min={0}
                  value={draft.checkout?.priceCents ?? 0}
                  onChange={(e) => update({ checkout: { ...(draft.checkout ?? emptyCheckout()), priceCents: Number(e.target.value) } })}
                />
              </Field>
              <Field label="Moeda" id="el-cur">
                <input
                  id="el-cur" maxLength={3}
                  value={draft.checkout?.currency ?? 'BRL'}
                  onChange={(e) => update({ checkout: { ...(draft.checkout ?? emptyCheckout()), currency: e.target.value.toUpperCase() } })}
                />
              </Field>
            </div>
          </>
        )}
      </fieldset>
    </Card>
  );
}

function emptyCheckout(): NonNullable<ElementModel['checkout']> {
  return { provider: 'custom', productId: '', priceCents: 0, currency: 'BRL', bumpUrl: '' };
}

function defaultFields(): FormFieldModel[] {
  return [
    { id: 'name', name: 'name', label: 'Nome', type: 'text', required: true, placeholder: 'Seu nome', options: [] },
    { id: 'email', name: 'email', label: 'E-mail', type: 'email', required: true, placeholder: 'voce@email.com', options: [] },
  ];
}

function FormFieldsEditor({
  value, onChange,
}: {
  value: NonNullable<ElementModel['form']>;
  onChange: (form: NonNullable<ElementModel['form']>) => void;
}): JSX.Element {
  return (
    <div>
      {value.fields.map((field, index) => (
        <div className="row" key={field.id + index} style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <Field label="Rótulo" id={`f-label-${index}`}>
              <input
                id={`f-label-${index}`}
                value={field.label}
                onChange={(e) => {
                  const fields = [...value.fields];
                  fields[index] = { ...field, label: e.target.value };
                  onChange({ ...value, fields });
                }}
              />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Tipo" id={`f-type-${index}`}>
              <select
                id={`f-type-${index}`}
                value={field.type}
                onChange={(e) => {
                  const fields = [...value.fields];
                  fields[index] = { ...field, type: e.target.value };
                  onChange({ ...value, fields });
                }}
              >
                <option value="text">Texto</option>
                <option value="email">E-mail</option>
                <option value="tel">Telefone</option>
                <option value="select">Seleção</option>
                <option value="checkbox">Confirmação</option>
              </select>
            </Field>
          </div>
          <label className="checkline" style={{ marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => {
                const fields = [...value.fields];
                fields[index] = { ...field, required: e.target.checked };
                onChange({ ...value, fields });
              }}
            />
            obrigatório
          </label>
          <button
            type="button" className="btn ghost small" style={{ marginBottom: 14 }}
            onClick={() => onChange({ ...value, fields: value.fields.filter((_, i) => i !== index) })}
          >
            Remover
          </button>
        </div>
      ))}
      <button
        type="button" className="btn secondary small"
        onClick={() =>
          onChange({
            ...value,
            fields: [
              ...value.fields,
              {
                id: `field_${value.fields.length + 1}`,
                name: `campo_${value.fields.length + 1}`,
                label: 'Novo campo', type: 'text', required: false, placeholder: '', options: [],
              },
            ],
          })
        }
      >
        Adicionar campo
      </button>
      <div className="grid cols-2" style={{ marginTop: 14 }}>
        <Field label="Texto do botão" id="f-submit">
          <input id="f-submit" value={value.submitLabel} onChange={(e) => onChange({ ...value, submitLabel: e.target.value })} />
        </Field>
        <Field label="Mensagem de sucesso" id="f-success">
          <input id="f-success" value={value.successMessage} onChange={(e) => onChange({ ...value, successMessage: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}
